import React,{useState,useEffect,useCallback,useMemo} from 'react';
import {inventoryAPI,branchAPI,productAPI} from '../utils/api';
import {PageHeader,SearchBar,DataTable,Badge,Modal,FormInput,fmt,fmtDate,FilterButtons} from '../components/common';
import {useLang} from '../i18n';
import {Plus,Minus,AlertTriangle,ArrowDownToLine,ArrowUpFromLine,History,Image,
  BarChart2,RefreshCw,ChevronDown,ChevronUp,Package,Layers,TrendingDown,
  TrendingUp,DollarSign,FileText,Download,Filter,X,CheckCircle} from 'lucide-react';
import toast from 'react-hot-toast';
import {useAuth} from '../context/AuthContext';

const MOVE_TYPES=['All','in','out','adjustment','transfer_in','transfer_out','return','damage'];
const MC={in:'success',out:'danger',adjustment:'warning',transfer_in:'info',transfer_out:'accent',return:'primary',damage:'danger'};

/* ─── UoM Conversion helper ─────────────────────────────────── */
// Given a base quantity and product's UoM list, return breakdown in each UoM
function convertToUoms(baseQty, product) {
  if(!product?.uoms?.length) return [];
  // Sort by conversion_factor descending (largest unit first)
  const sorted = [...product.uoms]
    .filter(pu=>pu.uom&&parseFloat(pu.conversion_factor)>0)
    .sort((a,b)=>parseFloat(b.conversion_factor)-parseFloat(a.conversion_factor));
  
  const results = [];
  let remaining = baseQty;
  for(const pu of sorted){
    const factor = parseFloat(pu.conversion_factor);
    const qty = Math.floor(remaining / factor);
    remaining = remaining % factor;
    results.push({uomName:pu.uom.name,uomAbbr:pu.uom.abbreviation,qty,factor,price:parseFloat(pu.price)||0});
  }
  // Leftover in base UoM
  if(remaining>0||results.length===0){
    const baseUom = product.uom;
    results.push({uomName:baseUom?.name||'unit',uomAbbr:baseUom?.abbreviation||'pcs',qty:remaining,factor:1,price:parseFloat(product.sale_price)||0,isBase:true});
  }
  return results.filter(r=>r.qty>0);
}

// Get all UoM rows for a product (always show all UoMs, compute qty)
function allUomRows(baseQty, product) {
  const rows = [];
  if(!product)return rows;
  
  const allUoms = [...(product.uoms||[])].sort((a,b)=>parseFloat(b.conversion_factor)-parseFloat(a.conversion_factor));
  
  for(const pu of allUoms){
    const factor = parseFloat(pu.conversion_factor)||1;
    const qty = baseQty / factor;
    rows.push({
      uomId:pu.uom_id,uomName:pu.uom?.name||'—',uomAbbr:pu.uom?.abbreviation||'',
      qty,qtyDisplay:qty%1===0?qty:qty.toFixed(3),
      factor,price:parseFloat(pu.price)||0,
      stockValue:(baseQty/factor)*(parseFloat(pu.price)||0),
    });
  }
  // Always add base UoM row
  const baseUom = product.uom;
  if(baseUom){
    rows.push({
      uomId:baseUom.id,uomName:baseUom.name,uomAbbr:baseUom.abbreviation,
      qty:baseQty,qtyDisplay:baseQty,factor:1,price:parseFloat(product.sale_price)||0,
      stockValue:baseQty*(parseFloat(product.sale_price)||0),isBase:true,
    });
  }
  return rows;
}

/* ─── UoM Stock Cell (expandable) ──────────────────────────── */
function UomStockCell({qty, product}){
  const[open,setOpen]=useState(false);
  const baseUom=product?.uom;
  const hasConversions=(product?.uoms?.length||0)>0;

  if(!hasConversions){
    return<span style={{fontWeight:700,fontSize:14}}>{qty} <span style={{fontSize:11,color:'var(--td)',fontWeight:400}}>{baseUom?.abbreviation||''}</span></span>;
  }

  const breakdown=convertToUoms(qty,product);
  const breakdownText=breakdown.map(b=>`${b.qty} ${b.uomAbbr}`).join(' + ');

  return(
    <div>
      <div style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer'}} onClick={()=>setOpen(v=>!v)}>
        <span style={{fontWeight:700,fontSize:14}}>{qty}</span>
        <span style={{fontSize:11,color:'var(--td)',fontWeight:400}}>{baseUom?.abbreviation||'pcs'}</span>
        <span style={{fontSize:10,padding:'1px 5px',borderRadius:4,background:'var(--pm)',color:'var(--primary)',fontWeight:600,display:'flex',alignItems:'center',gap:2}}>
          <Layers size={9}/>{open?<ChevronUp size={9}/>:<ChevronDown size={9}/>}
        </span>
      </div>
      {open&&(
        <div style={{marginTop:5,padding:'6px 10px',background:'var(--s2)',borderRadius:7,border:'1px solid var(--border)',minWidth:160}}>
          <div style={{fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:5}}>CONVERTED TO:</div>
          {breakdown.map((b,i)=>(
            <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:3}}>
              <span style={{fontSize:11,color:'var(--tm)',fontWeight:600}}>{b.uomName}</span>
              <span style={{fontSize:12,fontWeight:800,color:'var(--primary)'}}>{b.qty} <span style={{fontWeight:400,color:'var(--td)'}}>{b.uomAbbr}</span></span>
            </div>
          ))}
          <div style={{borderTop:'1px solid var(--border)',paddingTop:4,marginTop:4,fontSize:10,color:'var(--td)'}}>= {breakdownText}</div>
        </div>
      )}
    </div>
  );
}

/* ─── Stock Report Section ──────────────────────────────────── */
function StockReportTab({data,branches,products}){
  const[branchF,setBranchF]=useState('');
  const[catF,setCatF]=useState('');
  const[search,setSearch]=useState('');
  const[viewMode,setViewMode]=useState('summary'); // summary | byuom | valuation | lowstock
  const[expandedRows,setExpandedRows]=useState({});
  const{t}=useLang();
  const{hasPermission}=useAuth();
  const canSeeCost=hasPermission('cost_price.view');
  const toggleRow=id=>setExpandedRows(e=>({...e,[id]:!e[id]}));

  // Filter data
  const filtered=useMemo(()=>{
    let d=data;
    if(branchF)d=d.filter(r=>String(r.branch_id)===branchF);
    if(search){const q=search.toLowerCase();d=d.filter(r=>(r.product?.name||'').toLowerCase().includes(q)||(r.product?.sku||'').toLowerCase().includes(q));}
    return d;
  },[data,branchF,search]);

  // Aggregate for summary view
  const summary=useMemo(()=>{
    const map={};
    filtered.forEach(rec=>{
      const pid=rec.product_id;
      if(!map[pid])map[pid]={product:rec.product,branches:[],totalQty:0,totalValue:0};
      map[pid].branches.push({branch:rec.branch,qty:rec.quantity,cost:parseFloat(rec.cost_price||0),location:rec.location});
      map[pid].totalQty+=rec.quantity;
      map[pid].totalValue+=rec.quantity*parseFloat(rec.cost_price||0);
    });
    return Object.values(map).sort((a,b)=>b.totalQty-a.totalQty);
  },[filtered]);

  const branchOpts=[{value:'',label:t('inventory.allBranches')},...branches.map(b=>({value:String(b.id),label:b.name}))];

  // Stats
  const totalItems=filtered.length;
  const totalValue=filtered.reduce((s,r)=>s+r.quantity*parseFloat(r.cost_price||0),0);
  const totalSaleValue=filtered.reduce((s,r)=>s+r.quantity*parseFloat(r.product?.sale_price||0),0);
  const lowStockCount=filtered.filter(r=>r.quantity<=(r.product?.reorder_level||10)&&r.quantity>0).length;
  const outOfStockCount=filtered.filter(r=>r.quantity<=0).length;

  // Export CSV
  const exportCSV=()=>{
    const rows=[['SKU','Product','Branch','Base UoM','Qty (base)','Cost Price','Stock Value (cost)','Sale Price','Stock Value (sale)',...(filtered.some(r=>r.product?.uoms?.length>0)?['UoM Breakdown']:[])].join(',')];
    filtered.forEach(r=>{
      const breakdown=convertToUoms(r.quantity,r.product).map(b=>`${b.qty}${b.uomAbbr}`).join('+');
      rows.push([
        r.product?.sku||'',
        `"${r.product?.name||''}"`,
        `"${r.branch?.name||''}"`,
        r.product?.uom?.abbreviation||'pcs',
        r.quantity,
        parseFloat(r.cost_price||0).toFixed(2),
        (r.quantity*parseFloat(r.cost_price||0)).toFixed(2),
        parseFloat(r.product?.sale_price||0).toFixed(2),
        (r.quantity*parseFloat(r.product?.sale_price||0)).toFixed(2),
        breakdown?`"${breakdown}"`:''
      ].join(','));
    });
    const blob=new Blob([rows.join('\n')],{type:'text/csv'});
    const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download=`stock-report-${new Date().toISOString().split('T')[0]}.csv`;a.click();
  };

  return(
    <div>
      {/* Stat cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:10,marginBottom:18}}>
        {[
          {label:t('inventory.kpi.totalSkus'),value:summary.length,icon:Package,color:'var(--primary)'},
          {label:t('inventory.kpi.stockRecords'),value:totalItems,icon:Layers,color:'var(--info)'},
          ...(canSeeCost?[{label:t('inventory.kpi.costValue'),value:fmt(totalValue),icon:DollarSign,color:'var(--warning)'}]:[]),
          {label:t('inventory.kpi.saleValue'),value:fmt(totalSaleValue),icon:TrendingUp,color:'var(--success)'},
          {label:t('inventory.kpi.lowStock'),value:lowStockCount,icon:TrendingDown,color:'var(--warning)'},
          {label:t('inventory.kpi.outOfStock'),value:outOfStockCount,icon:AlertTriangle,color:'var(--danger)'},
        ].map(({label,value,icon:Icon,color})=>(
          <div key={label} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,padding:'12px 14px',display:'flex',alignItems:'center',gap:10}}>
            <div style={{width:34,height:34,borderRadius:8,background:color+'22',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Icon size={15} color={color}/></div>
            <div><div style={{fontSize:10,color:'var(--td)',fontWeight:600,letterSpacing:.3,textTransform:'uppercase'}}>{label}</div><div style={{fontSize:15,fontWeight:800,marginTop:1}}>{value}</div></div>
          </div>
        ))}
      </div>

      {/* Filters + view mode */}
      <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
        <div style={{flex:1,minWidth:200}}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search product name or SKU..."/>
        </div>
        <select className="form-select" value={branchF} onChange={e=>setBranchF(e.target.value)} style={{minWidth:160}}>
          {branchOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" style={{display:'flex',alignItems:'center',gap:6}} onClick={exportCSV}>
          <Download size={13}/> Export CSV
        </button>
      </div>

      {/* View mode tabs */}
      <div style={{display:'flex',gap:6,marginBottom:16,borderBottom:'1px solid var(--border)',paddingBottom:0}}>
        {[
          {key:'summary',label:t('inventory.reportTabs.summary'),icon:<Package size={13}/>},
          {key:'byuom',label:t('inventory.reportTabs.byuom'),icon:<Layers size={13}/>},
          {key:'valuation',label:t('inventory.reportTabs.valuation'),icon:<DollarSign size={13}/>},
          {key:'lowstock',label:t('inventory.reportTabs.lowstock'),icon:<AlertTriangle size={13}/>},
        ].map(v=>(
          <button key={v.key} onClick={()=>setViewMode(v.key)}
            style={{display:'flex',alignItems:'center',gap:5,padding:'8px 14px',fontSize:12,fontWeight:600,
              border:'none',borderBottom:`2px solid ${viewMode===v.key?'var(--primary)':'transparent'}`,
              background:'transparent',cursor:'pointer',color:viewMode===v.key?'var(--primary)':'var(--tm)',
              marginBottom:-1,transition:'color .15s'}}>
            {v.icon}{v.label}
          </button>
        ))}
      </div>

      {/* ── SUMMARY VIEW ── */}
      {viewMode==='summary'&&(
        <div>
          {summary.length===0
            ?<div style={{textAlign:'center',padding:40,color:'var(--td)'}}>No stock records found</div>
            :summary.map(item=>{
              const isOpen=expandedRows[item.product?.id];
              const prod=item.product;
              const baseUom=prod?.uom;
              const hasConv=(prod?.uoms?.length||0)>0;
              const low=item.totalQty<=(prod?.reorder_level||10)&&item.totalQty>0;
              const out=item.totalQty<=0;

              return(
                <div key={prod?.id} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,marginBottom:8,overflow:'hidden'}}>
                  {/* Header row */}
                  <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',cursor:'pointer'}}
                    onClick={()=>hasConv&&toggleRow(prod?.id)}>
                    {/* Image */}
                    <div style={{width:40,height:40,borderRadius:8,overflow:'hidden',flexShrink:0,background:'var(--s2)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                      {prod?.image_url?<img src={prod.image_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<Package size={16} color="var(--td)"/>}
                    </div>
                    {/* Product info */}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:2,flexWrap:'wrap'}}>
                        <span style={{fontWeight:700,fontSize:14}}>{prod?.name||'—'}</span>
                        <span style={{fontSize:10,fontFamily:'monospace',color:'var(--accent)',background:'var(--am)',padding:'1px 6px',borderRadius:4}}>{prod?.sku}</span>
                        {out&&<span style={{fontSize:10,fontWeight:700,color:'var(--danger)',background:'var(--dm)',padding:'1px 6px',borderRadius:4}}>OUT OF STOCK</span>}
                        {low&&!out&&<span style={{fontSize:10,fontWeight:700,color:'var(--warning)',background:'var(--wm)',padding:'1px 6px',borderRadius:4}}>LOW STOCK</span>}
                      </div>
                      <div style={{display:'flex',gap:12,fontSize:11,color:'var(--td)',flexWrap:'wrap'}}>
                        <span>{item.branches.length} branch{item.branches.length!==1?'es':''}</span>
                        {baseUom&&<span>Base: {baseUom.name} ({baseUom.abbreviation})</span>}
                        {hasConv&&<span style={{color:'var(--primary)'}}>+{prod.uoms.length} UoM conversion{prod.uoms.length!==1?'s':''}</span>}
                        <span>Reorder at: {prod?.reorder_level||10}</span>
                      </div>
                    </div>
                    {/* Qty + value */}
                    <div style={{textAlign:'right',flexShrink:0}}>
                      <div style={{fontSize:18,fontWeight:900,color:out?'var(--danger)':low?'var(--warning)':'var(--success)'}}>{item.totalQty} <span style={{fontSize:12,fontWeight:400,color:'var(--td)'}}>{baseUom?.abbreviation||'pcs'}</span></div>
                      {canSeeCost&&<div style={{fontSize:11,color:'var(--td)',marginTop:2}}>Cost: {fmt(item.totalValue)}</div>}
                      {hasConv&&<div style={{fontSize:10,color:'var(--primary)',marginTop:2,display:'flex',alignItems:'center',gap:3,justifyContent:'flex-end'}}><Layers size={9}/>UoM breakdown{isOpen?<ChevronUp size={9}/>:<ChevronDown size={9}/>}</div>}
                    </div>
                  </div>

                  {/* Branch breakdown */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:8,padding:'0 16px 12px',background:'var(--s2)',borderTop:'1px solid var(--border)'}}>
                    <div style={{gridColumn:'1/-1',fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginTop:10,marginBottom:2}}>BY BRANCH</div>
                    {item.branches.map((b,i)=>(
                      <div key={i} style={{background:'var(--surface)',borderRadius:8,padding:'8px 12px',border:'1px solid var(--border)'}}>
                        <div style={{fontSize:12,fontWeight:700,marginBottom:3}}>{b.branch?.name||'—'}</div>
                        <div style={{fontSize:15,fontWeight:800,color:b.qty<=0?'var(--danger)':b.qty<=(prod?.reorder_level||10)?'var(--warning)':'var(--success)'}}>{b.qty} <span style={{fontSize:11,fontWeight:400,color:'var(--td)'}}>{baseUom?.abbreviation||''}</span></div>
                        {b.location&&<div style={{fontSize:10,color:'var(--td)',marginTop:2}}>📍 {b.location.name}</div>}
                        {canSeeCost&&<div style={{fontSize:10,color:'var(--td)',marginTop:1}}>Cost/unit: {fmt(b.cost)}</div>}
                      </div>
                    ))}
                  </div>

                  {/* UoM breakdown expanded */}
                  {isOpen&&hasConv&&(()=>{
                    const rows=allUomRows(item.totalQty,prod);
                    return(
                      <div style={{padding:'12px 16px',borderTop:'1px solid var(--border)',background:'var(--pm)'}}>
                        <div style={{fontSize:11,fontWeight:700,color:'var(--primary)',letterSpacing:.4,marginBottom:10,display:'flex',alignItems:'center',gap:6}}><Layers size={13}/> UOM CONVERSION BREAKDOWN — {prod.name}</div>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:8}}>
                          {rows.map((row,i)=>(
                            <div key={i} style={{background:'var(--surface)',borderRadius:10,padding:'10px 12px',border:`1px solid ${row.isBase?'var(--primary)':'var(--border)'}`,position:'relative'}}>
                              {row.isBase&&<div style={{position:'absolute',top:4,right:6,fontSize:9,fontWeight:700,color:'var(--primary)'}}>BASE</div>}
                              <div style={{fontSize:10,color:'var(--td)',marginBottom:4,fontWeight:600}}>{row.uomName}</div>
                              <div style={{fontSize:20,fontWeight:900,color:'var(--text)'}}>{row.qtyDisplay}</div>
                              <div style={{fontSize:11,color:'var(--td)',marginTop:1}}>{row.uomAbbr}</div>
                              {row.factor>1&&<div style={{fontSize:10,color:'var(--td)',marginTop:3}}>1 {row.uomAbbr} = {row.factor} {prod.uom?.abbreviation||'pcs'}</div>}
                              {row.price>0&&<div style={{fontSize:11,color:'var(--success)',marginTop:4,fontWeight:700}}>{fmt(row.price)} / {row.uomAbbr}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
        </div>
      )}

      {/* ── BY UoM VIEW ── */}
      {viewMode==='byuom'&&(
        <div>
          <div style={{fontSize:12,color:'var(--td)',marginBottom:14,display:'flex',alignItems:'center',gap:6}}>
            <Layers size={13}/> Shows stock quantity converted to every unit of measure configured per product.
          </div>
          {filtered.length===0?<div style={{textAlign:'center',padding:40,color:'var(--td)'}}>No records</div>:
          filtered.map(rec=>{
            const prod=rec.product;
            const baseUom=prod?.uom;
            const rows=allUomRows(rec.quantity,prod);
            const low=rec.quantity<=(prod?.reorder_level||10)&&rec.quantity>0;
            const out=rec.quantity<=0;
            if(rows.length===0)return null;
            return(
              <div key={rec.id} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,marginBottom:8,padding:'14px 16px'}}>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
                  {prod?.image_url?<img src={prod.image_url} alt="" style={{width:36,height:36,borderRadius:7,objectFit:'cover',flexShrink:0}}/>:<div style={{width:36,height:36,borderRadius:7,background:'var(--s2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Package size={14} color="var(--td)"/></div>}
                  <div style={{flex:1}}>
                    <span style={{fontWeight:700}}>{prod?.name}</span>
                    <span style={{fontSize:11,color:'var(--accent)',fontFamily:'monospace',marginLeft:8}}>{prod?.sku}</span>
                  </div>
                  <Badge variant="primary">{rec.branch?.name}</Badge>
                  {out&&<Badge variant="danger">OUT</Badge>}
                  {low&&!out&&<Badge variant="warning">LOW</Badge>}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:8}}>
                  {/* Always show base first */}
                  <div style={{background:'var(--pm)',borderRadius:9,padding:'10px 12px',border:'1px solid var(--primary)33'}}>
                    <div style={{fontSize:10,fontWeight:700,color:'var(--primary)',marginBottom:4,textTransform:'uppercase'}}>{baseUom?.name||'Base'} <span style={{opacity:.6}}>(base)</span></div>
                    <div style={{fontSize:22,fontWeight:900,color:out?'var(--danger)':low?'var(--warning)':'var(--text)'}}>{rec.quantity}</div>
                    <div style={{fontSize:11,color:'var(--td)'}}>{baseUom?.abbreviation||'pcs'}</div>
                    <div style={{fontSize:11,color:'var(--success)',marginTop:4,fontWeight:700}}>{fmt(prod?.sale_price||0)}<span style={{fontWeight:400,color:'var(--td)'}}> / {baseUom?.abbreviation||'pcs'}</span></div>
                  </div>
                  {rows.filter(r=>!r.isBase).map((row,i)=>(
                    <div key={i} style={{background:'var(--s2)',borderRadius:9,padding:'10px 12px',border:'1px solid var(--border)'}}>
                      <div style={{fontSize:10,fontWeight:700,color:'var(--tm)',marginBottom:4,textTransform:'uppercase'}}>{row.uomName}</div>
                      <div style={{fontSize:22,fontWeight:900,color:'var(--text)'}}>{row.qtyDisplay}</div>
                      <div style={{fontSize:11,color:'var(--td)'}}>{row.uomAbbr}</div>
                      <div style={{fontSize:10,color:'var(--td)',marginTop:3}}>1 {row.uomAbbr} = {row.factor} {baseUom?.abbreviation}</div>
                      {row.price>0&&<div style={{fontSize:11,color:'var(--success)',marginTop:4,fontWeight:700}}>{fmt(row.price)}<span style={{fontWeight:400,color:'var(--td)'}}> / {row.uomAbbr}</span></div>}
                    </div>
                  ))}
                  {(prod?.uoms?.length||0)===0&&(
                    <div style={{background:'var(--s2)',borderRadius:9,padding:'10px 12px',border:'1px dashed var(--border)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <span style={{fontSize:11,color:'var(--td)'}}>No UoM conversions</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── VALUATION VIEW ── */}
      {viewMode==='valuation'&&(
        <div>
          <div style={{background:'var(--s2)',borderRadius:12,padding:16,marginBottom:16,display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:10}}>
            <div>
              <div style={{fontSize:10,color:'var(--td)',fontWeight:700,letterSpacing:.4,marginBottom:4}}>TOTAL COST VALUE</div>
              <div style={{fontSize:22,fontWeight:900,color:'var(--warning)'}}>{canSeeCost?fmt(totalValue):'—'}</div>
              <div style={{fontSize:11,color:'var(--td)'}}>at cost price</div>
            </div>
            <div>
              <div style={{fontSize:10,color:'var(--td)',fontWeight:700,letterSpacing:.4,marginBottom:4}}>TOTAL SALE VALUE</div>
              <div style={{fontSize:22,fontWeight:900,color:'var(--success)'}}>{fmt(totalSaleValue)}</div>
              <div style={{fontSize:11,color:'var(--td)'}}>at sale price</div>
            </div>
            <div>
              <div style={{fontSize:10,color:'var(--td)',fontWeight:700,letterSpacing:.4,marginBottom:4}}>POTENTIAL PROFIT</div>
              <div style={{fontSize:22,fontWeight:900,color:'var(--primary)'}}>{canSeeCost?fmt(totalSaleValue-totalValue):'—'}</div>
              <div style={{fontSize:11,color:'var(--td)'}}>if sold at sale price</div>
            </div>
            <div>
              <div style={{fontSize:10,color:'var(--td)',fontWeight:700,letterSpacing:.4,marginBottom:4}}>AVG MARGIN</div>
              <div style={{fontSize:22,fontWeight:900,color:'var(--accent)'}}>{canSeeCost&&totalSaleValue>0?((totalSaleValue-totalValue)/totalSaleValue*100).toFixed(1)+'%':'—'}</div>
              <div style={{fontSize:11,color:'var(--td)'}}>gross margin</div>
            </div>
          </div>
          <DataTable
            columns={[
              {key:'img',label:'',render:r=>r.product?.image_url?<img src={r.product.image_url} alt="" style={{width:30,height:30,borderRadius:6,objectFit:'cover'}}/>:<div style={{width:30,height:30,borderRadius:6,background:'var(--s2)',display:'flex',alignItems:'center',justifyContent:'center'}}><Package size={12} color="var(--td)"/></div>},
              {key:'sku',label:t('inventory.col.sku'),render:r=><span style={{color:'var(--accent)',fontFamily:'monospace',fontSize:11}}>{r.product?.sku}</span>},
              {key:'product',label:t('inventory.col.product'),render:r=><span style={{fontWeight:600}}>{r.product?.name||'—'}</span>},
              {key:'branch',label:t('inventory.col.branch'),render:r=>r.branch?.name||'—'},
              {key:'uom',label:'Base UoM',render:r=>r.product?.uom?.abbreviation||'pcs'},
              {key:'qty',label:t('inventory.col.qty'),render:r=><span style={{fontWeight:700}}>{r.quantity}</span>},
              ...(canSeeCost?[{key:'cost',label:t('inventory.col.unitCost'),render:r=>fmt(r.cost_price)}]:[]),
              {key:'sale',label:'Sale Price',render:r=>fmt(r.product?.sale_price||0)},
              ...(canSeeCost?[{key:'costVal',label:t('inventory.kpi.costValue'),render:r=><span style={{fontWeight:700,color:'var(--warning)'}}>{fmt(r.quantity*parseFloat(r.cost_price||0))}</span>}]:[]),
              {key:'saleVal',label:t('inventory.kpi.saleValue'),render:r=><span style={{fontWeight:700,color:'var(--success)'}}>{fmt(r.quantity*parseFloat(r.product?.sale_price||0))}</span>},
              ...(canSeeCost?[{key:'margin',label:'Margin',render:r=>{
                const s=parseFloat(r.product?.sale_price||0),cv=parseFloat(r.cost_price||0);
                const m=s>0?((s-cv)/s*100).toFixed(1):0;
                return<Badge variant={m>40?'success':m>20?'warning':'danger'}>{m}%</Badge>;
              }}]:[]),
              {key:'uomValues',label:'UoM Values',render:r=>{
                const rows=allUomRows(r.quantity,r.product).filter(x=>!x.isBase&&x.price>0);
                if(!rows.length)return<span style={{color:'var(--td)',fontSize:11}}>—</span>;
                return<div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                  {rows.map((row,i)=>(
                    <span key={i} style={{fontSize:10,padding:'2px 6px',borderRadius:5,background:'var(--pm)',color:'var(--primary)',fontWeight:600}}>
                      {row.qtyDisplay} {row.uomAbbr} = {fmt(row.stockValue)}
                    </span>
                  ))}
                </div>;
              }},
            ]}
            data={filtered}
          />
        </div>
      )}

      {/* ── LOW STOCK VIEW ── */}
      {viewMode==='lowstock'&&(()=>{
        const low=filtered.filter(r=>r.quantity>0&&r.quantity<=(r.product?.reorder_level||10));
        const out=filtered.filter(r=>r.quantity<=0);
        return(
          <div>
            {/* Out of stock section */}
            <div style={{marginBottom:20}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                <div style={{width:8,height:8,borderRadius:'50%',background:'var(--danger)'}}/>
                <span style={{fontSize:13,fontWeight:700,color:'var(--danger)'}}>OUT OF STOCK ({out.length})</span>
              </div>
              {out.length===0?<div style={{padding:'14px 16px',background:'var(--sm)',borderRadius:8,fontSize:12,color:'var(--success)',display:'flex',alignItems:'center',gap:8}}><CheckCircle size={14}/> No out-of-stock items</div>:
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:8}}>
                {out.map(r=>(
                  <div key={r.id} style={{background:'var(--surface)',border:'2px solid var(--danger)',borderRadius:10,padding:'12px 14px'}}>
                    <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
                      {r.product?.image_url?<img src={r.product.image_url} alt="" style={{width:32,height:32,borderRadius:6,objectFit:'cover',flexShrink:0}}/>:<div style={{width:32,height:32,borderRadius:6,background:'var(--dm)',flexShrink:0}}/>}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.product?.name}</div>
                        <div style={{fontSize:10,color:'var(--td)',fontFamily:'monospace'}}>{r.product?.sku}</div>
                        <Badge variant="primary" style={{marginTop:4}}>{r.branch?.name}</Badge>
                      </div>
                      <span style={{fontSize:20,fontWeight:900,color:'var(--danger)',flexShrink:0}}>0</span>
                    </div>
                    <div style={{marginTop:8,fontSize:10,color:'var(--td)',display:'flex',gap:10}}>
                      <span>Reorder: {r.product?.reorder_level}</span>
                      <span>Safety: {r.product?.safety_stock}</span>
                    </div>
                  </div>
                ))}
              </div>}
            </div>

            {/* Low stock section */}
            <div>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                <div style={{width:8,height:8,borderRadius:'50%',background:'var(--warning)'}}/>
                <span style={{fontSize:13,fontWeight:700,color:'var(--warning)'}}>LOW STOCK — BELOW REORDER LEVEL ({low.length})</span>
              </div>
              {low.length===0?<div style={{padding:'14px 16px',background:'var(--sm)',borderRadius:8,fontSize:12,color:'var(--success)',display:'flex',alignItems:'center',gap:8}}><CheckCircle size={14}/> All items above reorder level</div>:
              <DataTable columns={[
                {key:'img',label:'',render:r=>r.product?.image_url?<img src={r.product.image_url} alt="" style={{width:28,height:28,borderRadius:5,objectFit:'cover'}}/>:<div style={{width:28,height:28,borderRadius:5,background:'var(--wm)'}}/>},
                {key:'product',label:t('inventory.col.product'),render:r=><div><div style={{fontWeight:600}}>{r.product?.name}</div><div style={{fontSize:10,color:'var(--td)',fontFamily:'monospace'}}>{r.product?.sku}</div></div>},
                {key:'branch',label:t('inventory.col.branch'),render:r=>r.branch?.name},
                {key:'qty',label:t('inventory.col.onHand'),render:r=><span style={{fontWeight:800,color:'var(--warning)',fontSize:15}}>{r.quantity} {r.product?.uom?.abbreviation}</span>},
                {key:'reorder',label:'Reorder At',render:r=><span style={{color:'var(--td)'}}>{r.product?.reorder_level}</span>},
                {key:'needed',label:'Need to Order',render:r=>{
                  const needed=Math.max(0,(r.product?.reorder_level||10)*2-r.quantity);
                  return<span style={{fontWeight:700,color:'var(--primary)'}}>{needed} {r.product?.uom?.abbreviation}</span>;
                }},
                {key:'uomBreakdown',label:'Stock in UoMs',render:r=>{
                  const rows=convertToUoms(r.quantity,r.product);
                  if(!rows.length)return<span style={{color:'var(--td)'}}>—</span>;
                  return<div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                    {rows.map((row,i)=>(
                      <span key={i} style={{fontSize:11,fontWeight:700,padding:'2px 7px',borderRadius:5,background:'var(--wm)',color:'var(--warning)'}}>
                        {row.qty} {row.uomAbbr}
                      </span>
                    ))}
                  </div>;
                }},
                ...(canSeeCost?[{key:'value',label:'Stock Value',render:r=><span style={{color:'var(--warning)',fontWeight:700}}>{fmt(r.quantity*parseFloat(r.cost_price||0))}</span>}]:[]),
              ]} data={low}/>}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════ */
export default function InventoryPage(){
  const{t}=useLang();
  const{isOwner,myBranchId,myBranch,hasPermission}=useAuth();
  const canSeeCost=hasPermission('cost_price.view');
  const[data,setData]=useState([]);const[branches,setBranches]=useState([]);const[products,setProducts]=useState([]);
  const[ld,setLd]=useState(true);const[branchF,setBranchF]=useState('');const[lowOnly,setLowOnly]=useState(false);
  const[tab,setTab]=useState('stock');
  const[modal,setModal]=useState(null);
  const[stockForm,setStockForm]=useState({product_id:'',branch_id:'',quantity:1,unit_cost:0,reference_type:'manual',batch_number:'',serial_number:'',expiry_date:'',notes:''});
  const[movements,setMovements]=useState([]);const[mvLd,setMvLd]=useState(false);const[mvTypeF,setMvTypeF]=useState('All');
  const[mvProduct,setMvProduct]=useState('');const[mvBranch,setMvBranch]=useState('');const[mvDateFrom,setMvDateFrom]=useState('');const[mvDateTo,setMvDateTo]=useState('');

  useEffect(()=>{
    Promise.all([branchAPI.getAll({limit:100}),productAPI.getAll({limit:500})])
      .then(([b,p])=>{setBranches(b.data.data||[]);setProducts(p.data.data||[])})
      .catch(()=>{});
  },[]);

  const load=useCallback(async()=>{
    try{
      const p={limit:500};
      if(branchF)p.branch_id=branchF;
      if(lowOnly)p.low_stock='true';
      const r=await inventoryAPI.getAll(p);
      setData(r.data.data||[]);
    }catch{toast.error('Failed')}finally{setLd(false)};
  },[branchF,lowOnly]);
  useEffect(()=>{load()},[load]);

  const loadMovements=useCallback(async()=>{
    setMvLd(true);
    try{
      const p={limit:300};
      if(mvTypeF!=='All')p.type=mvTypeF;
      if(mvProduct)p.product_id=mvProduct;
      if(mvBranch)p.branch_id=mvBranch;
      if(mvDateFrom)p.date_from=mvDateFrom;
      if(mvDateTo)p.date_to=mvDateTo;
      const r=await inventoryAPI.getMovements(p);
      setMovements(r.data.data||[]);
    }catch{toast.error('Failed')}finally{setMvLd(false)};
  },[mvTypeF,mvProduct,mvBranch,mvDateFrom,mvDateTo]);
  useEffect(()=>{if(tab==='movements')loadMovements()},[tab,loadMovements]);

  const branchOpts=[{value:'',label:t('inventory.allBranches')},...branches.map(b=>({value:String(b.id),label:b.name}))];
  const prodOpts=[{value:'',label:'— Select Product —'},...products.map(p=>({value:String(p.id),label:`${p.sku} — ${p.name}`}))];
  const SF=(k,v)=>setStockForm(f=>({...f,[k]:v}));

  const openStockIn=(inv)=>{
    setStockForm({product_id:inv?String(inv.product_id):'',branch_id:inv?String(inv.branch_id):String(branches[0]?.id||''),quantity:1,uom_id:'',uom_conversion_factor:1,uom_label:'',unit_cost:inv?parseFloat(inv.cost_price)||0:0,reference_type:'purchase',batch_number:'',serial_number:'',expiry_date:'',notes:''});
    setModal('stockIn');
  };
  const doStockIn=async()=>{
    if(!stockForm.product_id||!stockForm.branch_id||!stockForm.quantity)return toast.error(t('inventory.productBranchQtyRequired'));
    const factor=parseFloat(stockForm.uom_conversion_factor)||1;
    const baseQty=+(parseFloat(stockForm.quantity)*factor).toFixed(4);
    const uomNote=factor>1?` (${stockForm.quantity} ${stockForm.uom_label||'units'} × ${factor} = ${baseQty} base units)`:` (${stockForm.quantity} units)`;
    try{
      await inventoryAPI.stockIn({product_id:parseInt(stockForm.product_id),branch_id:parseInt(stockForm.branch_id),quantity:baseQty,uom_id:stockForm.uom_id?parseInt(stockForm.uom_id):null,uom_conversion_factor:factor,unit_cost:parseFloat(stockForm.unit_cost)||0,reference_type:stockForm.reference_type,batch_number:stockForm.batch_number||null,serial_number:stockForm.serial_number||null,expiry_date:stockForm.expiry_date||null,notes:(stockForm.notes||'')+uomNote});
      toast.success(`Stock IN: +${baseQty} base units${factor>1?' ('+stockForm.quantity+' '+stockForm.uom_label+')':''}`);setModal(null);load();
    }catch(e){toast.error(e.response?.data?.error||'Failed')}
  };

  const openStockOut=(inv)=>{
    setStockForm({product_id:inv?String(inv.product_id):'',branch_id:inv?String(inv.branch_id):String(branches[0]?.id||''),quantity:1,uom_id:'',uom_conversion_factor:1,uom_label:'',reference_type:'sale',notes:''});
    setModal('stockOut');
  };
  const doStockOut=async()=>{
    if(!stockForm.product_id||!stockForm.branch_id||!stockForm.quantity)return toast.error(t('inventory.productBranchQtyRequired'));
    const factor=parseFloat(stockForm.uom_conversion_factor)||1;
    const baseQty=+(parseFloat(stockForm.quantity)*factor).toFixed(4);
    const uomNote=factor>1?` (${stockForm.quantity} ${stockForm.uom_label||'units'} × ${factor} = ${baseQty} base units)`:` (${stockForm.quantity} units)`;
    try{
      await inventoryAPI.stockOut({product_id:parseInt(stockForm.product_id),branch_id:parseInt(stockForm.branch_id),quantity:baseQty,uom_id:stockForm.uom_id?parseInt(stockForm.uom_id):null,uom_conversion_factor:factor,reference_type:stockForm.reference_type,notes:(stockForm.notes||'')+uomNote});
      toast.success(`Stock OUT: -${baseQty} base units${factor>1?' ('+stockForm.quantity+' '+stockForm.uom_label+')':''}`);setModal(null);load();
    }catch(e){toast.error(e.response?.data?.error||'Failed')}
  };


  const TABS=[
    {key:'stock',label:t('inventory.tabs.stock'),icon:<Package size={13}/>},
    {key:'reports',label:t('inventory.tabs.reports'),icon:<BarChart2 size={13}/>},
    {key:'movements',label:t('inventory.tabs.movements'),icon:<History size={13}/>},
  ];

  return(<div>
    <PageHeader title={t('inventory.title')} subtitle={t('inventory.subtitle',{n:data.length})} action={
      <div className="flex gap-2">
        <button className="btn btn-success" onClick={()=>openStockIn(null)}><ArrowDownToLine size={16}/> {t('inventory.stockIn')}</button>
        <button className="btn btn-danger" onClick={()=>openStockOut(null)}><ArrowUpFromLine size={16}/> {t('inventory.stockOut')}</button>
      </div>
    }/>

    {/* Tabs */}
    <div style={{display:'flex',gap:6,marginBottom:16,borderBottom:'1px solid var(--border)',paddingBottom:0}}>
      {TABS.map(t=>(
        <button key={t.key} onClick={()=>setTab(t.key)}
          style={{display:'flex',alignItems:'center',gap:6,padding:'8px 14px',fontSize:13,fontWeight:600,
            border:'none',borderBottom:`2px solid ${tab===t.key?'var(--primary)':'transparent'}`,
            background:'transparent',cursor:'pointer',color:tab===t.key?'var(--primary)':'var(--tm)',
            marginBottom:-1,transition:'color .15s'}}>
          {t.icon}{t.label}
        </button>
      ))}
    </div>

    {/* ══ STOCK LEVELS TAB ══ */}
    {tab==='stock'&&<>
      <div className="filter-bar">
        <div style={{minWidth:200}}><FormInput value={branchF} onChange={setBranchF} options={branchOpts}/></div>
        <button className={`filter-btn ${lowOnly?'active':''}`} onClick={()=>setLowOnly(!lowOnly)} style={{display:'flex',alignItems:'center',gap:4}}>
          <AlertTriangle size={14}/> {t('inventory.lowStockOnly')}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13}/></button>
      </div>
      <DataTable loading={ld} columns={[
        {key:'img',label:'',render:r=>r.product?.image_url?<img src={r.product.image_url} alt="" style={{width:32,height:32,borderRadius:6,objectFit:'cover'}}/>:<div style={{width:32,height:32,borderRadius:6,background:'var(--s2)',display:'flex',alignItems:'center',justifyContent:'center'}}><Image size={12} color="var(--td)"/></div>},
        {key:'product',label:t('inventory.col.product'),render:r=><div><div className="font-bold">{r.product?.name||'—'}</div><div className="text-dim" style={{fontSize:11}}>{r.product?.sku||''}</div></div>},
        {key:'branch',label:t('inventory.col.branch'),render:r=><Badge variant="primary">{r.branch?.name||'—'}</Badge>},
        {key:'location',label:t('inventory.col.location'),render:r=>r.location?.name||'—'},
        {key:'quantity',label:t('inventory.col.onHand'),render:r=><UomStockCell qty={r.quantity} product={r.product}/>},
        {key:'reserved',label:t('inventory.col.reserved'),render:r=>r.reserved_quantity||0},
        {key:'available',label:t('inventory.col.available'),render:r=><span className="font-bold">{r.quantity-(r.reserved_quantity||0)}</span>},
        {key:'reorder',label:t('inventory.col.reorder'),render:r=>r.product?.reorder_level||'—'},
        ...(canSeeCost?[
          {key:'cost',label:t('inventory.col.unitCost'),render:r=>fmt(r.cost_price)},
          {key:'value',label:t('inventory.col.value'),render:r=><span className="font-bold">{fmt(r.quantity*parseFloat(r.cost_price||0))}</span>},
        ]:[]),
        {key:'batch',label:t('inventory.col.batch'),render:r=>r.batch_number||'—'},
        {key:'expiry',label:t('inventory.col.expiry'),render:r=>r.expiry_date?<span style={{color:new Date(r.expiry_date)<new Date()?'var(--danger)':'inherit'}}>{fmtDate(r.expiry_date)}</span>:'—'},
      ]} data={data} actions={r=>(<div className="flex gap-2">
        <button className="btn btn-success btn-sm" onClick={()=>openStockIn(r)} title={t('inventory.stockIn')}><ArrowDownToLine size={14}/> {t('inventory.in')}</button>
        <button className="btn btn-danger btn-sm" onClick={()=>openStockOut(r)} title={t('inventory.stockOut')}><ArrowUpFromLine size={14}/> {t('inventory.out')}</button>
      </div>)}/>
    </>}

    {/* ══ STOCK REPORTS TAB ══ */}
    {tab==='reports'&&(
      ld?<div style={{textAlign:'center',padding:40,color:'var(--td)',display:'flex',alignItems:'center',justifyContent:'center',gap:8}}><RefreshCw size={16} style={{animation:'spin 1s linear infinite'}}/> Loading inventory...</div>:
      <StockReportTab data={data} branches={branches} products={products}/>
    )}

    {/* ══ MOVEMENT HISTORY TAB ══ */}
    {tab==='movements'&&<>
      <div className="filter-bar" style={{flexWrap:'wrap',gap:8}}>
        <FormInput value={mvBranch} onChange={setMvBranch} options={branchOpts}/>
        <FormInput value={mvProduct} onChange={setMvProduct} options={[{value:'',label:t('inventory.allProducts')},...products.map(p=>({value:String(p.id),label:`${p.sku} — ${p.name}`}))]}/>
        <input className="form-input" type="date" value={mvDateFrom} onChange={e=>setMvDateFrom(e.target.value)} {...{placeholder:t('inventory.fromDate')}} style={{width:140}}/>
        <input className="form-input" type="date" value={mvDateTo} onChange={e=>setMvDateTo(e.target.value)} {...{placeholder:t('inventory.toDate')}} style={{width:140}}/>
        <FilterButtons options={MOVE_TYPES} active={mvTypeF} onChange={setMvTypeF}/>
        <button className="btn btn-ghost btn-sm" onClick={loadMovements}><RefreshCw size={13}/></button>
        {(mvDateFrom||mvDateTo||mvProduct||mvBranch||mvTypeF!=='All')&&(
          <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={()=>{setMvDateFrom('');setMvDateTo('');setMvProduct('');setMvBranch('');setMvTypeF('All');}}>
            <X size={13}/> Clear
          </button>
        )}
      </div>
      <DataTable loading={mvLd} columns={[
        {key:'date',label:t('inventory.col.date'),render:r=>fmtDate(r.created_at)},
        {key:'img',label:'',render:r=>r.product?.image_url?<img src={r.product.image_url} alt="" style={{width:28,height:28,borderRadius:5,objectFit:'cover'}}/>:<div style={{width:28,height:28,borderRadius:5,background:'var(--s2)'}}/>},
        {key:'product',label:t('inventory.col.product'),render:r=><div><div className="font-bold">{r.product?.name||'—'}</div><div className="text-dim" style={{fontSize:10}}>{r.product?.sku||''}</div></div>},
        {key:'branch',label:t('inventory.col.branch'),render:r=>r.branch?.name||'—'},
        {key:'type',label:t('inventory.col.type'),render:r=><Badge variant={MC[r.type]||'primary'}>{r.type.replace(/_/g,' ').toUpperCase()}</Badge>},
        {key:'quantity',label:t('inventory.col.qty'),render:r=><span style={{fontWeight:700,fontSize:15,color:['in','transfer_in','return'].includes(r.type)?'var(--success)':'var(--danger)'}}>{['in','transfer_in','return'].includes(r.type)?'+':'-'}{r.quantity}</span>},
        ...(canSeeCost?[
          {key:'unit_cost',label:t('inventory.col.unitCost'),render:r=>r.unit_cost?fmt(r.unit_cost):'—'},
          {key:'total',label:t('inventory.totalValue'),render:r=>r.unit_cost?fmt(r.quantity*parseFloat(r.unit_cost)):'—'},
        ]:[]),
        {key:'reference',label:t('inventory.col.reference'),render:r=>r.reference_type?<span className="text-muted">{r.reference_type}{r.reference_id?` #${r.reference_id}`:''}</span>:'—'},
        {key:'notes',label:t('inventory.col.notes'),render:r=>r.notes?<span style={{maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',display:'inline-block'}}>{r.notes}</span>:'—'},
      ]} data={movements}/>
    </>}

    {/* ══ STOCK IN MODAL ══ */}
    <Modal open={modal==='stockIn'} onClose={()=>setModal(null)} title={t('inventory.stockInTitle')} width={560}>
      <div style={{padding:10,background:'var(--sm)',borderRadius:8,marginBottom:14,display:'flex',alignItems:'center',gap:8}}>
        <ArrowDownToLine size={20} color="var(--success)"/><span style={{fontWeight:600,color:'var(--success)'}}>{t('inventory.addingStock')}</span>
      </div>
      <div className="form-grid">
        <FormInput {...{label:t('inventory.productRequired')}} value={String(stockForm.product_id||'')} onChange={v=>{SF('product_id',v);SF('uom_id','');SF('uom_conversion_factor',1);SF('uom_label','');}} options={prodOpts} required/>
        <FormInput {...{label:t('inventory.branchRequired')}} value={String(stockForm.branch_id||'')} onChange={v=>SF('branch_id',v)} options={branchOpts.filter(o=>o.value)} required/>
      </div>
      {/* UoM selector — shown when product has alternative UoMs */}
      {(()=>{
        const prod=products.find(x=>x.id===parseInt(stockForm.product_id));
        if(!prod)return null;
        const baseLabel=prod.uom?.abbreviation||'unit';
        const altUoms=(prod.uoms||[]).filter(pu=>pu.uom).map(pu=>({id:pu.uom_id,label:pu.uom.abbreviation,name:pu.uom.name,factor:parseFloat(pu.conversion_factor)||1}));
        if(!altUoms.length)return(<div style={{marginBottom:12,padding:'8px 12px',background:'var(--s2)',borderRadius:8,fontSize:12,color:'var(--td)'}}><strong>Unit:</strong> {baseLabel} (base unit)</div>);
        const selFactor=parseFloat(stockForm.uom_conversion_factor)||1;
        const baseQty=+(parseFloat(stockForm.quantity||0)*selFactor).toFixed(4);
        return(
          <div style={{marginBottom:12,padding:'12px 14px',background:'var(--s2)',borderRadius:10,border:'1px solid var(--border)'}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.5,marginBottom:10}}>{t('inventory.selectUom')}</div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:10}}>
              {/* Base UoM option */}
              <button onClick={()=>{SF('uom_id','');SF('uom_conversion_factor',1);SF('uom_label',baseLabel);}}
                style={{padding:'6px 14px',borderRadius:8,border:`2px solid ${!stockForm.uom_id?'var(--success)':'var(--border)'}`,
                  background:!stockForm.uom_id?'var(--sm)':'var(--surface)',cursor:'pointer',fontSize:12,fontWeight:700,
                  color:!stockForm.uom_id?'var(--success)':'var(--td)',display:'flex',alignItems:'center',gap:5}}>
                {baseLabel} <span style={{fontWeight:400,fontSize:10}}>(base)</span>
              </button>
              {/* Alt UoM options */}
              {altUoms.map(u=>(
                <button key={u.id} onClick={()=>{SF('uom_id',String(u.id));SF('uom_conversion_factor',u.factor);SF('uom_label',u.label);}}
                  style={{padding:'6px 14px',borderRadius:8,border:`2px solid ${String(stockForm.uom_id)===String(u.id)?'var(--success)':'var(--border)'}`,
                    background:String(stockForm.uom_id)===String(u.id)?'var(--sm)':'var(--surface)',cursor:'pointer',fontSize:12,fontWeight:700,
                    color:String(stockForm.uom_id)===String(u.id)?'var(--success)':'var(--td)',display:'flex',alignItems:'center',gap:5}}>
                  {u.label} <span style={{fontWeight:400,fontSize:10}}>×{u.factor}</span>
                </button>
              ))}
            </div>
            {selFactor>1&&parseFloat(stockForm.quantity)>0&&(
              <div style={{padding:'8px 12px',borderRadius:8,background:'var(--sm)',border:'1px solid rgba(34,197,94,.3)',display:'flex',alignItems:'center',gap:8,fontSize:12}}>
                <ArrowDownToLine size={14} color="var(--success)"/>
                <span style={{color:'var(--td)'}}>{t('inventory.receiving')}</span>
                <strong style={{color:'var(--success)',fontSize:14}}>{stockForm.quantity} {stockForm.uom_label}</strong>
                <span style={{color:'var(--td)'}}>{`= ${t('inventory.willAdd')}`}</span>
                <strong style={{color:'var(--success)',fontSize:14}}>{baseQty} {baseLabel}</strong>
                <span style={{color:'var(--td)',fontSize:10}}>{t('inventory.toBaseStock')}</span>
              </div>
            )}
          </div>
        );
      })()}
      <div className="form-grid">
        <div>
          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>
            {t('inventory.quantityLabel')} {stockForm.uom_id&&stockForm.uom_label?<span style={{fontWeight:400,color:'var(--success)',textTransform:'none'}}>in {stockForm.uom_label}</span>:''}
          </label>
          <input type="number" min={0} step={stockForm.uom_conversion_factor>1?1:0.001}
            value={String(stockForm.quantity)}
            onChange={e=>SF('quantity',parseFloat(e.target.value)||0)}
            style={{fontSize:14,padding:'10px 12px',height:44,width:'100%',background:'var(--s2)',border:'1.5px solid var(--success)',borderRadius:9,color:'var(--text)',boxSizing:'border-box',fontWeight:700}}/>
        </div>
        {canSeeCost&&<FormInput {...{label:t('inventory.unitCostUsd')}} value={String(stockForm.unit_cost)} onChange={v=>SF('unit_cost',parseFloat(v)||0)} type="number"/>}
        <FormInput {...{label:t('inventory.referenceType')}} value={stockForm.reference_type} onChange={v=>SF('reference_type',v)} options={[{value:'purchase',label:t('inventory.refTypes.purchase')},{value:'return',label:t('inventory.refTypes.return')},{value:'transfer',label:t('inventory.refTypes.transfer')},{value:'adjustment',label:t('inventory.refTypes.adjustment')},{value:'manual',label:t('inventory.refTypes.manual')}]}/>
        <FormInput {...{label:t('inventory.batchNumber')}} value={stockForm.batch_number} onChange={v=>SF('batch_number',v)} placeholder="LOT-2026-001"/>
        <FormInput {...{label:t('inventory.serialNumber')}} value={stockForm.serial_number} onChange={v=>SF('serial_number',v)} placeholder="SN-xxxx"/>
        <FormInput {...{label:t('inventory.expiryDate')}} value={stockForm.expiry_date} onChange={v=>SF('expiry_date',v)} type="date"/>
      </div>
      <FormInput {...{label:t('common.notes')}} value={stockForm.notes} onChange={v=>SF('notes',v)} type="textarea" {...{placeholder:t('inventory.notesPlaceholder')}}/>
      {/* Total value preview */}
      {(()=>{
        const factor=parseFloat(stockForm.uom_conversion_factor)||1;
        const baseQty=+(parseFloat(stockForm.quantity||0)*factor).toFixed(4);
        const cost=parseFloat(stockForm.unit_cost)||0;
        if(canSeeCost&&baseQty>0&&cost>0)return(
          <div style={{padding:10,background:'var(--s2)',borderRadius:8,textAlign:'right',marginTop:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:11,color:'var(--td)'}}>
              {factor>1?`${stockForm.quantity} ${stockForm.uom_label} × ${factor} = ${baseQty} ${products.find(x=>x.id===parseInt(stockForm.product_id))?.uom?.abbreviation||'units'}`:`${baseQty} units`}
            </span>
            <span><span className="text-muted">{t('inventory.totalValue')}: </span><span className="font-bold text-success" style={{fontSize:16}}>{fmt(baseQty*cost)}</span></span>
          </div>
        );
        return null;
      })()}
      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
        <button className="btn btn-success" onClick={doStockIn}><ArrowDownToLine size={16}/> {t('inventory.receiveStock')}</button>
      </div>
    </Modal>

    {/* ══ STOCK OUT MODAL ══ */}
    <Modal open={modal==='stockOut'} onClose={()=>setModal(null)} title={t('inventory.stockOutTitle')} width={480}>
      <div style={{padding:10,background:'var(--dm)',borderRadius:8,marginBottom:14,display:'flex',alignItems:'center',gap:8}}>
        <ArrowUpFromLine size={20} color="var(--danger)"/><span style={{fontWeight:600,color:'var(--danger)'}}>{t('inventory.removingStock')}</span>
      </div>
      <div className="form-grid">
        <FormInput {...{label:t('inventory.productRequired')}} value={String(stockForm.product_id||'')} onChange={v=>{SF('product_id',v);SF('uom_id','');SF('uom_conversion_factor',1);SF('uom_label','');}} options={prodOpts} required/>
        <FormInput {...{label:t('inventory.branchRequired')}} value={String(stockForm.branch_id||'')} onChange={v=>SF('branch_id',v)} options={branchOpts.filter(o=>o.value)} required/>
      </div>
      {/* UoM selector */}
      {(()=>{
        const prod=products.find(x=>x.id===parseInt(stockForm.product_id));
        if(!prod)return null;
        const baseLabel=prod.uom?.abbreviation||'unit';
        const altUoms=(prod.uoms||[]).filter(pu=>pu.uom).map(pu=>({id:pu.uom_id,label:pu.uom.abbreviation,name:pu.uom.name,factor:parseFloat(pu.conversion_factor)||1}));
        if(!altUoms.length)return(<div style={{marginBottom:12,padding:'8px 12px',background:'var(--s2)',borderRadius:8,fontSize:12,color:'var(--td)'}}><strong>Unit:</strong> {baseLabel} (base unit)</div>);
        const selFactor=parseFloat(stockForm.uom_conversion_factor)||1;
        const baseQty=+(parseFloat(stockForm.quantity||0)*selFactor).toFixed(4);
        return(
          <div style={{marginBottom:12,padding:'12px 14px',background:'var(--s2)',borderRadius:10,border:'1px solid var(--border)'}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.5,marginBottom:10}}>{t('inventory.selectUom')}</div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:10}}>
              <button onClick={()=>{SF('uom_id','');SF('uom_conversion_factor',1);SF('uom_label',baseLabel);}}
                style={{padding:'6px 14px',borderRadius:8,border:`2px solid ${!stockForm.uom_id?'var(--danger)':'var(--border)'}`,
                  background:!stockForm.uom_id?'var(--dm)':'var(--surface)',cursor:'pointer',fontSize:12,fontWeight:700,
                  color:!stockForm.uom_id?'var(--danger)':'var(--td)',display:'flex',alignItems:'center',gap:5}}>
                {baseLabel} <span style={{fontWeight:400,fontSize:10}}>(base)</span>
              </button>
              {altUoms.map(u=>(
                <button key={u.id} onClick={()=>{SF('uom_id',String(u.id));SF('uom_conversion_factor',u.factor);SF('uom_label',u.label);}}
                  style={{padding:'6px 14px',borderRadius:8,border:`2px solid ${String(stockForm.uom_id)===String(u.id)?'var(--danger)':'var(--border)'}`,
                    background:String(stockForm.uom_id)===String(u.id)?'var(--dm)':'var(--surface)',cursor:'pointer',fontSize:12,fontWeight:700,
                    color:String(stockForm.uom_id)===String(u.id)?'var(--danger)':'var(--td)',display:'flex',alignItems:'center',gap:5}}>
                  {u.label} <span style={{fontWeight:400,fontSize:10}}>×{u.factor}</span>
                </button>
              ))}
            </div>
            {selFactor>1&&parseFloat(stockForm.quantity)>0&&(
              <div style={{padding:'8px 12px',borderRadius:8,background:'var(--dm)',border:'1px solid rgba(220,38,38,.3)',display:'flex',alignItems:'center',gap:8,fontSize:12}}>
                <ArrowUpFromLine size={14} color="var(--danger)"/>
                <span style={{color:'var(--td)'}}>{t('inventory.issuing')}</span>
                <strong style={{color:'var(--danger)',fontSize:14}}>{stockForm.quantity} {stockForm.uom_label}</strong>
                <span style={{color:'var(--td)'}}>{`= ${t('inventory.willRemove')}`}</span>
                <strong style={{color:'var(--danger)',fontSize:14}}>{baseQty} {baseLabel}</strong>
                <span style={{color:'var(--td)',fontSize:10}}>{t('inventory.fromBaseStock')}</span>
              </div>
            )}
          </div>
        );
      })()}
      <div className="form-grid">
        <div>
          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>
            {t('inventory.quantityLabel')} {stockForm.uom_id&&stockForm.uom_label?<span style={{fontWeight:400,color:'var(--danger)',textTransform:'none'}}>in {stockForm.uom_label}</span>:''}
          </label>
          <input type="number" min={0} step={stockForm.uom_conversion_factor>1?1:0.001}
            value={String(stockForm.quantity)}
            onChange={e=>SF('quantity',parseFloat(e.target.value)||0)}
            style={{fontSize:14,padding:'10px 12px',height:44,width:'100%',background:'var(--s2)',border:'1.5px solid var(--danger)',borderRadius:9,color:'var(--text)',boxSizing:'border-box',fontWeight:700}}/>
        </div>
        <FormInput {...{label:t('inventory.reasonLabel')}} value={stockForm.reference_type} onChange={v=>SF('reference_type',v)} options={[{value:'sale',label:t('inventory.issueTypes.sale')},{value:'damage',label:t('inventory.issueTypes.damage')},{value:'transfer',label:t('inventory.issueTypes.transfer')},{value:'return_vendor',label:t('inventory.issueTypes.return_vendor')},{value:'manual',label:t('inventory.issueTypes.manual')}]}/>
      </div>
      <FormInput {...{label:t('common.notes')}} value={stockForm.notes} onChange={v=>SF('notes',v)} type="textarea" {...{placeholder:t('inventory.reasonPlaceholder')}}/>
      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
        <button className="btn btn-danger" onClick={doStockOut}><ArrowUpFromLine size={16}/> {t('inventory.issueStock')}</button>
      </div>
    </Modal>
  </div>);
}
